import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Search, Check, MoreHorizontal, User, Shield, X, Loader2, Crosshair } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import BaseInput from '@/components/uiKit/Inputs/BaseInput';
import { BaseIconButton } from '@/modules';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { DMTimestampTriangleIcon } from '@/components/shared/display/Icons';
import type { LegendSyncMode } from '@/modules/AssetsStudioPage/SensorFusion/hooks';
import { LegendModeToggle } from '@/modules/shared/DataLayers/components/filters/LegendModeToggle';
import { ChipsWithMore } from '@/modules/shared/DataLayers/components/filters/ChipsWithMore';

export interface FilterOption {
  value: string;
  label: string;
  enabled?: boolean;
  disabled?: boolean; // Option is disabled and cannot be changed
  source?: 'user' | 'Datamind';
  userDetails?: {
    name: string;
    email: string;
  };
  // Marker chip specific properties
  badge?: 'user' | string;
  canDelete?: boolean;
  markerId?: string;
  timestamp?: Date;
  color?: string;
}

export interface DataLayerFilter {
  id: string;
  label?: string;
  title?: string;
  type: 'dropdown' | 'chips' | 'search' | 'group' | 'expandable_legend';
  isMultiSelect?: boolean;
  isMultiSelected?: boolean;
  options: FilterOption[] | any[];
  defaultValue?: string | string[];
  handler?: (value: string | string[], filterConfig: DataLayerFilter) => void;
  onDelete?: (optionValue: string) => void; // For deletable chips (e.g., markers)
  resetHandler?: (resetCallback: () => void) => void; // For group filters to register reset callback
  chipDisplayLimit?: number; // Number of chips to show before "More" dropdown (default: 3)
  chipDisplayMode?: 'dropdown' | 'carousel'; // Display mode: dropdown (default) or carousel (horizontal scroll)
  searchConfig?: {
    enabled?: boolean;
    useLocalLegend?: boolean;
    minLength?: number;
    placeholder?: string;
  };
}

interface DataLayerFiltersProps {
  filters: DataLayerFilter[];
  onFilterChange?: (filterId: string, value: string | string[]) => void;
  className?: string;
  // Add props needed for sensor toggling like comparison table
  enabledSensors?: Record<string, boolean>;
  onToggleSensor?: (sectionId: string, sensorName: string) => void;
  onAddSensor?: (sectionId: string, sensorName: string) => void;
  sensorColors?: Record<string, string>;
  loadingSensors?: Set<string>; // Sensors currently loading data
  syncSectionLegends?: boolean;
  // Legend sync mode props
  legendSyncMode?: LegendSyncMode;
  onLegendModeToggle?: () => void;
  showLegendModeToggle?: boolean;
  focusedSensor?: string | {sensorName: string, sectionId: string} | null;
  onFocus?: (sensorName: string, sectionId?: string) => void;
  // Mutual exclusivity groups - array of filter IDs that should clear each other when selected
  mutuallyExclusiveChipFilters?: string[];
  // Loading state - disables focus mode toggle when data is loading
  isLoading?: boolean;
}

export const DataLayerFilters: React.FC<DataLayerFiltersProps> = ({
  filters,
  onFilterChange,
  className = '',
  enabledSensors = {},
  onToggleSensor,
  onAddSensor,
  sensorColors = {},
  loadingSensors = new Set(),
  syncSectionLegends,
  legendSyncMode = 'multi',
  onLegendModeToggle,
  showLegendModeToggle = false,
  focusedSensor = null,
  onFocus,
  mutuallyExclusiveChipFilters = [],
  isLoading = false,
}) => {
  const { t } = useTranslation();

  // CRITICAL DEBUG: Log mutually exclusive chip filters

  // Debug logging to see what we're receiving

  // Log search filter specifically
  const searchFilter = filters?.find(f => f.type === 'search');
  if (searchFilter) {
  } else {
  }

  // Track which sensor is currently loading
  const [loadingSensor, setLoadingSensor] = useState<string | null>(null);

  // State to control scroll-to functionality (enabled by default for better UX)
  const [scrollToEnabled, setScrollToEnabled] = useState<boolean>(true);

  // State to control search dropdown open state
  const [searchDropdownOpen, setSearchDropdownOpen] = useState<Record<string, boolean>>({});
  const [sortedOptionsCache, setSortedOptionsCache] = useState<Record<string, any[]>>({});

  // Clear loading state after a timeout OR when sensor becomes enabled
  useEffect(() => {
    if (loadingSensor) {
      const timer = setTimeout(() => {
        setLoadingSensor(null);
      }, 3000); // Clear after 3 seconds max
      return () => clearTimeout(timer);
    }
  }, [loadingSensor]);

  // Clear loading state when sensor becomes enabled
  useEffect(() => {
    if (loadingSensor && enabledSensors[loadingSensor]) {
      setLoadingSensor(null);
    }
  }, [loadingSensor, enabledSensors]);

  const [filterValues, setFilterValues] = useState<Record<string, string | string[]>>(() => {
    const initial: Record<string, string | string[]> = {};

    if (filters && Array.isArray(filters)) {
      // CRITICAL: For mutually exclusive chip filters, ensure only ONE is selected initially
      let firstSelectedChipFilterId: string | null = null;
      let firstSelectedChipValue: string | null = null;

      // First pass: find if any mutually exclusive chip has a selected value
      if (mutuallyExclusiveChipFilters.length > 0) {
        for (const filter of filters) {
          if (mutuallyExclusiveChipFilters.includes(filter.id) && filter.type === 'chips') {
            // Check if this filter has any enabled options
            const enabledOption = filter.options?.find((opt: any) => opt.enabled || opt.checked);
            if (enabledOption && !firstSelectedChipFilterId) {
              firstSelectedChipFilterId = filter.id;
              firstSelectedChipValue = enabledOption.value;
              break; // Only take the FIRST enabled chip
            }
          }
        }
      }

      // Second pass: initialize all filters
      filters.forEach((filter) => {
        if (filter.defaultValue !== undefined) {
          initial[filter.id] = filter.defaultValue;
        } else if (filter.type === 'chips') {
          // For mutually exclusive chip filters
          if (mutuallyExclusiveChipFilters.includes(filter.id)) {
            // Only set the first selected chip, clear all others
            if (filter.id === firstSelectedChipFilterId && firstSelectedChipValue) {
              initial[filter.id] = [firstSelectedChipValue];
            } else {
              initial[filter.id] = [];
            }
          } else {
            // Non-mutually-exclusive chips: use normal initialization
            initial[filter.id] = [];
          }
        } else if (filter.isMultiSelect || filter.type === 'search') {
          // Search filters always use multi-select behavior
          initial[filter.id] = [];
        } else {
          initial[filter.id] = filter.options?.[0]?.value || '';
        }
      });
    }

    return initial;
  });

  // State for search filters
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});

  // CRITICAL: Initialize new filters when they are added dynamically (e.g., carousel filter when markers added)
  useEffect(() => {
    if (!filters || filters.length === 0) return;

    setFilterValues((prev) => {
      const newValues = { ...prev };
      let hasChanges = false;

      filters.forEach((filter) => {
        // If filter doesn't exist in state yet, initialize it with default value
        if (!(filter.id in newValues)) {
          hasChanges = true;

          if (filter.defaultValue !== undefined) {
            newValues[filter.id] = filter.defaultValue;
          } else if (filter.type === 'chips') {
            // For chip filters, check if any option is enabled by default
            const enabledOption = filter.options?.find((opt: any) => opt.enabled || opt.checked);
            if (enabledOption) {
              newValues[filter.id] = [enabledOption.value];
            } else {
              newValues[filter.id] = [];
            }
          } else if (filter.isMultiSelect || filter.type === 'search') {
            newValues[filter.id] = [];
          } else {
            newValues[filter.id] = filter.options?.[0]?.value || '';
          }
        }
      });

      return hasChanges ? newValues : prev;
    });
  }, [filters]);

  // CRITICAL: Re-apply mutual exclusivity when filters or mutually exclusive list changes
  useEffect(() => {
    if (mutuallyExclusiveChipFilters.length > 0) {
      // Check if multiple mutually exclusive chips are selected
      const selectedChips = mutuallyExclusiveChipFilters.filter((filterId) => {
        const value = filterValues[filterId];
        return Array.isArray(value) && value.length > 0;
      });

      if (selectedChips.length > 1) {
        // Keep only the first selected chip, clear all others
        setFilterValues((prev) => {
          const newValues = { ...prev };
          const firstChip = selectedChips[0];

          mutuallyExclusiveChipFilters.forEach((filterId) => {
            if (filterId !== firstChip) {
              newValues[filterId] = [];
            }
          });

          return newValues;
        });
      }
    }
  }, [filters.length, mutuallyExclusiveChipFilters.join(',')]); // Re-run when filters change

  // Sync filterValues with defaultValue and enabled options when filter config changes
  // This handles cases like auto-selecting a chip after deletion or when selectedMarkerTimestamp changes
  useEffect(() => {
    // Use setState callback to access current filterValues without dependency issues
    setFilterValues((prevFilterValues) => {
      const updates: Record<string, string | string[]> = {};
      let hasUpdates = false;

      filters.forEach((filter) => {
        if (filter.type === 'chips') {
          const currentValue = prevFilterValues[filter.id];
          const currentArray = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);

          // ALWAYS check for enabled options first - this is the source of truth
          const enabledOption = filter.options?.find((opt: any) => opt.enabled === true);

          if (enabledOption) {
            // Found an enabled option - make sure it's selected
            if (!currentArray.includes(enabledOption.value)) {
              // Store as array to match initialization format
              updates[filter.id] = [enabledOption.value];
              hasUpdates = true;
            }
          } else if (filter.defaultValue !== undefined) {
            // No enabled option, fall back to defaultValue
            const newValue = filter.defaultValue;
            const newArray = Array.isArray(newValue) ? newValue : (newValue ? [newValue] : []);

            const isDifferent = newArray.length !== currentArray.length ||
              !newArray.every(v => currentArray.includes(v));

            if (isDifferent) {
              updates[filter.id] = newValue;
              hasUpdates = true;
            }
          }
        }
      });

      if (hasUpdates) {
        const newValues = {
          ...prevFilterValues,
          ...updates,
        };

        // Call onFilterChange for each updated filter (after state update)
        setTimeout(() => {
          Object.entries(updates).forEach(([filterId, value]) => {
            const filter = filters.find((f) => f.id === filterId);
            if (filter && onFilterChange) {
              onFilterChange(filterId, value);
            }
          });
        }, 0);

        return newValues;
      }

      return prevFilterValues;
    });
  }, [
    // Only depend on filter config changes - track enabled options
    filters.map((f) =>
      f.type === 'chips'
        ? `${f.id}:${f.options?.find((o: any) => o.enabled)?.value || 'none'}:${f.defaultValue || ''}`
        : ''
    ).join('|'),
    filters,
    onFilterChange
  ]);

  const handleFilterChange = useCallback(
    (filterId: string, value: string | string[], filter: DataLayerFilter) => {

      // CRITICAL: Handle mutual exclusivity for chip filters
      if (mutuallyExclusiveChipFilters.includes(filterId) && Array.isArray(value) && value.length > 0) {

        // Clear all other chip filters in the mutually exclusive group
        setFilterValues((prev) => {
          const newValues = { ...prev, [filterId]: value };

          mutuallyExclusiveChipFilters.forEach((otherFilterId) => {
            if (otherFilterId !== filterId) {
              newValues[otherFilterId] = [];
            }
          });

          return newValues;
        });
      } else {
        // Normal filter update
        setFilterValues((prev) => ({
          ...prev,
          [filterId]: value,
        }));
      }

      // Call the custom handler if provided
      if (filter.handler) {
        filter.handler(value, filter);
      }

      // Call the general onChange handler
      if (onFilterChange) {
        onFilterChange(filterId, value);
      } else {
      }
    },
    [onFilterChange, mutuallyExclusiveChipFilters]
  );

  const renderFilter = useCallback(
    (filter: DataLayerFilter) => {
      if (!filter) return null;

      const currentValue = filterValues[filter.id];

      // Skip group type as it's handled by NestedDataLayerFilters
      if (filter.type === 'group') {
        return null;
      }

      // Skip hidden filters (processed for backend but not displayed)
      if ((filter as any).hidden) {
        return null;
      }

      // Handle chips type (same as features in SensorChartToolbar)
      if (filter.type === 'chips') {
        const options = filter.options || [];
        const displayMode = filter.chipDisplayMode ?? 'dropdown';
        const basicFeaturesLimit = filter.chipDisplayLimit ?? 2;

        // Carousel mode: use ChipsWithMore component (chips + "More" dropdown)
        if (displayMode === 'carousel') {
          return (
            <ChipsWithMore
              key={filter.id}
              filterId={filter.id}
              options={options}
              currentValue={currentValue}
              isMultiSelect={filter.isMultiSelect}
              onFilterChange={handleFilterChange}
              onDelete={filter.onDelete}
              filter={filter}
              visibleChipsLimit={basicFeaturesLimit}
              moreLabel="More"
            />
          );
        }

        // Dropdown mode (default): show limited chips + "More" dropdown
        const basicFeatureOptions = options.slice(0, basicFeaturesLimit);
        const extraFeatureOptions = options.slice(basicFeaturesLimit);
        const currentArray = Array.isArray(currentValue) ? currentValue : [currentValue];
        const activeExtraFeatureOptions = extraFeatureOptions.filter((opt) => currentArray.includes(opt.value));
        const hasActiveExtraFeatures = activeExtraFeatureOptions.length > 0;
        const isDot = false;

        return (
          <div key={filter.id} className="flex gap-2 flex-wrap">
            {basicFeatureOptions.map((option) => {
              const isActive = currentArray.includes(option.value);
              const activeCount = currentArray.length;
              const isLastActive = isActive && activeCount === 1;
              // Check if option is disabled from metadata
              const isOptionDisabled = option.disabled === true;
              // For single select, never disable. For multi select, disable if it's the last active item
              const isDisabled = isOptionDisabled || (filter.isMultiSelect !== false && isLastActive);

              if (option.source === 'user' && option.userDetails) {
                return (
                  <TooltipProvider key={option.value}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="inline-block">
                          <Badge
                            variant="outline"
                            className={`px-3 py-1 text-xs bg-secondary hover:bg-secondary flex items-center gap-1 border-0 ${
                              isActive ? 'bg-primary-500 hover:bg-primary-500 text-white' : 'cursor-pointer'
                            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={() => {
                              if (isDisabled) return;
                              let newValue;
                              if (filter.isMultiSelect === false) {
                                // Single select - always set to clicked item (or empty if clicking active item)
                                newValue = isActive ? [] : [option.value];
                              } else {
                                // Multi select - add/remove from array
                                newValue = isActive
                                  ? currentArray.filter((v) => v !== option.value)
                                  : [...currentArray, option.value];
                              }
                              handleFilterChange(filter.id, newValue, filter);
                            }}
                          >
                            <User className="w-3 h-3" />
                            {option.label}
                            {/* Show X button for deletable marker chips */}
                            {option.canDelete && filter.onDelete && (
                              <button
                                className="ml-1 hover:bg-red-500/20 rounded-full p-0.5"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  filter.onDelete!(option.value);
                                }}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </Badge>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-xs">
                          <div>{option.userDetails.name}</div>
                          <div className="text-gray-400">{option.userDetails.email}</div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }

              return (
                <Badge
                  key={option.value}
                  variant="outline"
                  className={`px-3 py-1 text-xs bg-secondary hover:bg-secondary flex items-center gap-1 border-0 ${
                    isActive ? 'bg-primary-500 hover:bg-primary-500 text-white' : 'cursor-pointer'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => {
                    if (isDisabled) return;
                    let newValue;
                    if (filter.isMultiSelect === false) {
                      // Single select - always set to clicked item (or empty if clicking active item)
                      newValue = isActive ? [] : [option.value];
                    } else {
                      // Multi select - add/remove from array
                      newValue = isActive
                        ? currentArray.filter((v) => v !== option.value)
                        : [...currentArray, option.value];
                    }
                    handleFilterChange(filter.id, newValue, filter);
                  }}
                >
                  {option.source === 'user' ? (
                    <User className="w-3 h-3" />
                  ) : option.source === 'Datamind' ? (
                    <DMTimestampTriangleIcon />
                  ) : null}
                  {option.label}
                  {/* Show X button for deletable marker chips */}
                  {option.canDelete && filter.onDelete && (
                    <button
                      className="ml-1 hover:bg-red-500/20 rounded-full p-0.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        filter.onDelete!(option.value);
                      }}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </Badge>
              );
            })}

            {filter.options.length > 2 && !syncSectionLegends ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`relative px-3 py-1.5 pb-1 h-auto text-xs flex items-center bg-secondary rounded-full font-semibold gap-1.5 ${
                      !isDot && hasActiveExtraFeatures ? '!bg-primary-500 text-white' : ''
                    }`}
                  >
                    {hasActiveExtraFeatures ? (
                      <>
                        {activeExtraFeatureOptions[0].source === 'user' ? (
                          <User className="w-3 h-3" />
                        ) : activeExtraFeatureOptions[0].source === 'Datamind' ? (
                          <Shield className="w-3 h-3" />
                        ) : null}
                        {activeExtraFeatureOptions[0].label}
                        {activeExtraFeatureOptions.length > 1 && ` +${activeExtraFeatureOptions.length - 1}`}
                      </>
                    ) : (
                      t('filters.more')
                    )}
                    <MoreHorizontal size={14} />
                    {hasActiveExtraFeatures && isDot && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-primary-600 rounded-full border border-white" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                {/* Old: className="w-auto p-2 space-y-2" */}
                <DropdownMenuContent className="w-auto p-2 space-y-2">
                  {extraFeatureOptions.map((option) => {
                    const isActive = currentArray.includes(option.value);
                    const isOptionDisabled = option.disabled === true;

                    if (option.source === 'user' && option.userDetails) {
                      return (
                        <TooltipProvider key={option.value}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={`flex items-center space-x-2 p-1 rounded ${
                                  isOptionDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
                                }`}
                                onClick={() => {
                                  if (isOptionDisabled) return;
                                  let newValue;
                                  if (filter.isMultiSelect === false) {
                                    // Single select - always set to clicked item (or empty if clicking active item)
                                    newValue = isActive ? [] : [option.value];
                                  } else {
                                    // Multi select - add/remove from array
                                    newValue = isActive
                                      ? currentArray.filter((v) => v !== option.value)
                                      : [...currentArray, option.value];
                                  }
                                  handleFilterChange(filter.id, newValue, filter);
                                }}
                              >
                                <Check
                                  className={`w-4 h-4 text-primary-500 ${isActive ? 'opacity-100' : 'opacity-0'}`}
                                />
                                <div className="flex items-center gap-1 flex-1">
                                  <User className="w-3 h-3" />
                                  <span className="text-sm">{option.label}</span>
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs">
                                <div>{option.userDetails.name}</div>
                                <div className="text-gray-400">{option.userDetails.email}</div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    }

                    return (
                      <div
                        key={option.value}
                        className={`flex items-center space-x-2 p-1 rounded ${
                          isOptionDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          if (isOptionDisabled) return;
                          let newValue;
                          if (filter.isMultiSelect === false) {
                            // Single select - always set to clicked item (or empty if clicking active item)
                            newValue = isActive ? [] : [option.value];
                          } else {
                            // Multi select - add/remove from array
                            newValue = isActive
                              ? currentArray.filter((v) => v !== option.value)
                              : [...currentArray, option.value];
                          }
                          handleFilterChange(filter.id, newValue, filter);
                        }}
                      >
                        <Check className={`w-4 h-4 text-primary-500 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                        <div className="flex items-center gap-1 flex-1">
                          {option.source === 'user' ? (
                            <User className="w-3 h-3" />
                          ) : option.source === 'Datamind' ? (
                            <Shield className="w-3 h-3" />
                          ) : null}
                          <span className="text-sm">{option.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        );
      }

      // Handle search type with dropdown
      if (filter.type === 'search') {
        const searchQuery = searchQueries[filter.id] || '';

        const options = filter.options || [];


        // Show ALL options when dropdown first opens, filter when user searches
        let filteredOptions: any[] = [];

        if (sortedOptionsCache[filter.id]) {
          // Use sorted cache if available (dropdown is open)
          if (searchQuery.length > 0) {
            // Filter cached options by search query
            filteredOptions = sortedOptionsCache[filter.id].filter((option) =>
              option.label.toLowerCase().includes(searchQuery.toLowerCase())
            );
          } else {
            // Show all cached options when no search query
            filteredOptions = sortedOptionsCache[filter.id];
          }
        } else {
          // No cache yet (dropdown not opened), show filtered or all options
          if (searchQuery.length > 0) {
            filteredOptions = options.filter((option) =>
              option.label.toLowerCase().includes(searchQuery.toLowerCase())
            );
          } else {
            // Show all options initially
            filteredOptions = options;
          }
        }


        const filterLabel = filter.label || 'Search';

        return (
          <DropdownMenu
            key={filter.id}
            open={searchDropdownOpen[filter.id] || false}
            onOpenChange={(open) => {
              setSearchDropdownOpen((prev) => ({
                ...prev,
                [filter.id]: open,
              }));

              // Create/update sorted options cache every time dropdown opens
              if (open) {
                const sortedOptions = [...options].sort((a, b) => {
                  // First sort by enabled status (enabled items first)
                  const aEnabled = enabledSensors[a.label] || false;
                  const bEnabled = enabledSensors[b.label] || false;

                  if (aEnabled !== bEnabled) {
                    return aEnabled ? -1 : 1;
                  }

                  // Then sort alphabetically within each group
                  return a.label.localeCompare(b.label);
                });

                setSortedOptionsCache((prev) => ({
                  ...prev,
                  [filter.id]: sortedOptions,
                }));
              }

              // Clear search and cache when closing
              if (!open) {
                setSearchQueries((prev) => ({
                  ...prev,
                  [filter.id]: '',
                }));
                setSortedOptionsCache((prev) => {
                  const newCache = { ...prev };
                  delete newCache[filter.id];
                  return newCache;
                });
              }
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="py-0"
                data-testid={`graph_control_${generateTestId(filterLabel)}`}
              >
                <span className="flex items-center text-sm font-semibold gap-2">
                  {filterLabel}
                  {/* Counter disabled - uncomment to show enabled count
                  {enabledCount > 0 && !loadingSensor && (
                    <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full">
                      {enabledCount}
                    </span>
                  )}
                  */}
                  {loadingSensor && <Loader2 className="w-3 h-3 animate-spin text-primary-600" />}
                  <ChevronDown className="w-4 h-4" />
                </span>
              </Button>
            </DropdownMenuTrigger>
            {/* Old: className="w-72 max-h-[65vh] overflow-y-auto" */}
            <DropdownMenuContent className="w-72 max-h-[65vh] overflow-y-auto" align="start">
              <div className="p-3 space-y-3 [&_input[type='search']::-webkit-search-cancel-button]:appearance-none [&_input[type='search']::-webkit-search-decoration]:appearance-none">
                {/* Search input with scroll toggle button */}
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <BaseInput
                      dataTestid="search_box"
                      type="search"
                      placeholder={t('filters.searchSensors')}
                      value={searchQuery}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSearchQueries((prev) => {
                          const updated = {
                            ...prev,
                            [filter.id]: newValue,
                          };
                          return updated;
                        });
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      leftButton={{
                        element: Search as any,
                        onClick: undefined,
                        variant: 'ghost',
                      }}
                      rightButton={
                        searchQuery
                          ? {
                              element: X as any,
                              onClick: () => {
                                setSearchQueries((prev) => ({
                                  ...prev,
                                  [filter.id]: '',
                                }));
                              },
                              variant: 'ghost',
                              classNames: { button: 'min-w-5 min-h-5', icon: 'w-2.5 h-2.5' },
                            }
                          : undefined
                      }
                      variant="underline"
                    />
                  </div>

                  {/* Scroll toggle button with tooltip */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <BaseIconButton
                            dataTestid="search_auto_scroll"
                            icon={Crosshair}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setScrollToEnabled(!scrollToEnabled);
                            }}
                            isActive={false}
                            variant="ghost"
                            disabled={false} // Keep it clickable
                            classNames={{
                              button: `rounded-full ${!scrollToEnabled ? 'opacity-50' : ''}`,
                              icon: scrollToEnabled ? 'text-primary-600' : 'text-gray-400',
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>{scrollToEnabled ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Filtered options list */}
                <div className="max-h-72 overflow-y-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {filteredOptions.length > 0 ? (
                    <div className="space-y-0.5">
                      {filteredOptions.map((option, index) => {
                        // Get sensor name from option (same as comparison table)
                        const sensorName = option.label;
                        // Get enabled state from props (same as comparison table)
                        const isEnabled = enabledSensors[sensorName] || false;

                        const isLoading = loadingSensor === sensorName || loadingSensors.has(sensorName);
                        // Disable if loading OR if the option itself is disabled in metadata
                        const isDisabled =
                          option.disabled === true || loadingSensor !== null || loadingSensors.size > 0;

                        return (
                          <div
                            key={`${option.value}-${index}`}
                            className={`flex items-center justify-between py-2 px-3 rounded-md group ${
                              isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'
                            }`}
                            onClick={async (e) => {

                              // Don't close the popover when clicking items
                              e.stopPropagation();

                              // FIXED: Only prevent clicking THIS sensor if it's currently loading
                              if (loadingSensor === sensorName) {
                                return;
                              }

                              // Set loading state for this sensor (only when enabling)
                              if (!isEnabled) {
                                setLoadingSensor(sensorName);
                              }


                              // CRITICAL FIX: Pass BOTH sectionId AND sensorName to handlers!
                              const handler = onToggleSensor || onAddSensor;
                              if (handler) {
                                // Pass sectionId (graph ID) as first param, sensorName as second param
                                handler(option.sectionId || sensorName, sensorName);
                              } else {
                              }

                              // Only scroll when enabling a sensor, not when disabling
                              if (scrollToEnabled && !isEnabled) {
                                setSearchDropdownOpen((prev) => ({
                                  ...prev,
                                  [filter.id]: false,
                                }));

                                // Then scroll after dropdown is closed
                                if (option.section) {
                                  setTimeout(() => {
                                    // Try to find the section by its title
                                    const sectionElements = document.querySelectorAll('[data-section-title]');
                                    const targetSection = Array.from(sectionElements).find(
                                      (el) => el.getAttribute('data-section-title') === option.section
                                    );

                                    if (targetSection) {
                                      targetSection.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'center',
                                      });

                                      // Set focus to the target section to prevent focus jumping
                                      if (targetSection instanceof HTMLElement) {
                                        targetSection.setAttribute('tabindex', '-1');
                                        targetSection.focus({ preventScroll: true });
                                      }
                                    } else {
                                    }
                                  }, 600); // Wait for dropdown to close and graph to expand
                                }
                              }
                            }}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <div className="flex flex-col">
                                <span className={`text-sm font-medium ${isEnabled ? '' : 'text-muted-foreground'}`}>
                                  {option.label}
                                </span>
                                <div className="flex items-center gap-2">
                                  {option.unit && <span className="text-xs text-muted-foreground">{option.unit}</span>}
                                  {option.section && (
                                    <span className="text-xs text-muted-foreground">{option.section}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {isLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                            ) : isEnabled && !isLoading ? (
                              <div className="w-4 h-4 rounded-full flex items-center justify-center transition-colors pointer-events-none bg-primary-600">
                                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4">{t('filters.noMatchingItems')}</div>
                  )}
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }

      // Handle dropdown type (default)
      if (filter.isMultiSelect) {
        const multiValue = Array.isArray(currentValue) ? currentValue : [];
        const selectedLabels = filter.options.filter((opt) => multiValue.includes(opt.value)).map((opt) => opt.label);

        return (
          <DropdownMenu key={filter.id}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="py-0">
                <span className="flex items-center text-sm font-semibold gap-2">
                  {filter.label}: {selectedLabels.length > 0 ? selectedLabels.join(', ') : t('filters.all')}
                  <ChevronDown className="w-4 h-4" />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {filter.options.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={multiValue.includes(option.value)}
                  disabled={option.disabled === true}
                  onCheckedChange={(checked) => {
                    if (option.disabled) return;
                    const newValue = checked
                      ? [...multiValue, option.value]
                      : multiValue.filter((v) => v !== option.value);

                    if (newValue.length > 0) {
                      handleFilterChange(filter.id, newValue, filter);
                    }
                  }}
                >
                  <span className={option.disabled ? 'opacity-50' : ''}>{option.label}</span>
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      } else {
        const singleValue = Array.isArray(currentValue) ? currentValue[0] : currentValue;
        const selectedLabel = filter.options.find((opt) => opt.value === singleValue)?.label || filter.label;

        return (
          <DropdownMenu key={filter.id}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="py-0">
                <span className="flex items-center text-sm font-semibold gap-2">
                  {filter.label}: {selectedLabel}
                  <ChevronDown className="w-4 h-4" />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {filter.options.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  disabled={option.disabled === true}
                  onClick={() => {
                    if (option.disabled) return;
                    handleFilterChange(filter.id, option.value, filter);
                  }}
                  className={`${option.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'} ${
                    singleValue === option.value ? 'bg-gray-50' : ''
                  }`}
                >
                  <span className={option.disabled ? 'opacity-50' : ''}>{option.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    },
    [
      filterValues,
      handleFilterChange,
      searchQueries,
      scrollToEnabled,
      setScrollToEnabled,
      enabledSensors,
      onToggleSensor,
      onAddSensor,
      sensorColors,
      loadingSensor,
      loadingSensors,
      t,
      searchDropdownOpen,
      setSearchDropdownOpen,
    ]
  );

  if (!filters || filters.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      {filters.map((filter) => renderFilter(filter))}
      {showLegendModeToggle && onLegendModeToggle && (
        <LegendModeToggle mode={legendSyncMode} onToggle={onLegendModeToggle} />
      )}
    </div>
  );
};
